<template>
  <a-modal
    v-model:open="visible"
    :title="modalTitle"
    :width="850"
    :maskClosable="false"
    :closable="false"
    class="additional-terms-modal"
  >
    <div class="modal-content" ref="modalContentRef">
      <!-- 載入中狀態 -->
      <div v-if="loading" class="loading-container">
        <a-spin size="large" />
        <p class="loading-text">載入附加條款中...</p>
      </div>

      <!-- 錯誤狀態 -->
      <div v-else-if="error" class="error-container">
        <a-alert :message="error" type="error" />
        <a-button type="primary" @click="fetchTerms" class="retry-button"
          >重試</a-button
        >
      </div>

      <!-- 條款內容 -->
      <div v-else class="policy-content ck-content">
        <div v-html="policyContent"></div>
      </div>
    </div>

    <template #footer>
      <div class="modal-footer-container">
        <div class="checkbox-container">
          <a-checkbox v-model:checked="isChecked">
            我已閱讀並同意上述條款
          </a-checkbox>
        </div>
        <div class="button-group">
          <a-button @click="handleCancel">取消</a-button>
          <a-button type="primary" @click="handleAgree" :disabled="!isChecked">我同意</a-button>
        </div>
      </div>
    </template>
  </a-modal>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { message } from "ant-design-vue";
import { SITE_NAME } from "../constants/site";
import { contentAPI } from "../services/api";

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  standardName: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:open", "agree", "cancel"]);

const visible = ref(false);
const policyContent = ref("");
const dynamicTitle = ref("");
const currentVersion = ref("v1.0");
const loading = ref(true);
const error = ref(null);
const isChecked = ref(false);
const modalContentRef = ref(null);

const modalTitle = computed(() => {
  if (dynamicTitle.value) {
    return dynamicTitle.value;
  }
  return "臺灣健康資料空間 附加條款";
});

const defaultMockTerms = `
  <div class="terms-article">
    <h3>使用者條款</h3>
    <p style="color: #666; font-size: 13px; margin-bottom: 20px;">此頁面為前端 mock 版本，僅供 UI 檢視。</p>
    
    <h4>一、帳號</h4>
    <p>使用者需提供有效電子郵件與會員資料完成註冊流程，並妥善保管帳號憑證。</p>
    
    <h4>二、隱私</h4>
    <p>本平台將依隱私政策處理您的個人資料。使用特定服務（API 申請與標準下載、標準提案、個人證書下載）時所蒐集之姓名、單位、身分證字號、聯絡方式與 NDA 等資訊，僅限於資格審查、身分比對與聯繫使用。</p>

    <h4>三、智慧財產權與授權使用規範</h4>
    <p>本平台發布之醫學標準名詞、編碼及相關套件均受著作權法保護，下載與使用應遵守授權協議與 NDA 規範，嚴禁非授權之再散布行為。</p>
  </div>
`;

watch(
  () => props.open,
  (newVal) => {
    visible.value = newVal;
    if (newVal) {
      isChecked.value = false;
      fetchTerms();
      nextTick(() => {
        if (modalContentRef.value) {
          modalContentRef.value.scrollTop = 0;
        }
      });
    }
  }
);

watch(visible, (newVal) => {
  emit("update:open", newVal);
});

const fetchTerms = async () => {
  loading.value = true;
  error.value = null;
  try {
    if (contentAPI.getAdditionalTerms) {
      const res = await contentAPI.getAdditionalTerms();
      if (res?.data) {
        if (res.data.content_html) {
          policyContent.value = res.data.content_html;
        }
        if (res.data.title) {
          dynamicTitle.value = res.data.title;
        }
        if (res.data.version) {
          currentVersion.value = res.data.version;
        }
      } else {
        policyContent.value = defaultMockTerms;
      }
    } else {
      policyContent.value = defaultMockTerms;
    }
  } catch (err) {
    console.warn("載入附加條款失敗，使用預設文案:", err);
    policyContent.value = defaultMockTerms;
  } finally {
    loading.value = false;
  }
};

const handleAgree = () => {
  if (!isChecked.value) {
    message.warning("請勾選「我已閱讀並同意上述條款」");
    return;
  }
  emit("agree", currentVersion.value);
  visible.value = false;
};

const handleCancel = () => {
  emit("cancel");
  visible.value = false;
};
</script>

<style scoped>
.modal-content {
  max-height: 60vh;
  min-height: 280px;
  overflow-y: auto;
  padding: 20px 24px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  scrollbar-width: thin;
  scrollbar-color: #94a3b8 #f1f5f9;
}

.modal-content::-webkit-scrollbar {
  width: 8px;
}

.modal-content::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.modal-content::-webkit-scrollbar-thumb {
  background: #94a3b8;
  border-radius: 4px;
}

.modal-content::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
}

.loading-text {
  margin-top: 16px;
  color: #6b7280;
  font-size: 14px;
}

.error-container {
  padding: 24px 0;
  text-align: center;
}

.retry-button {
  margin-top: 16px;
}

/* 核心內容樣式：完全對齊後台 CKEditor / 富文本編輯器呈現 */
.policy-content {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  line-height: 1.75;
  color: #1f2937;
  font-size: 14.5px;
}

:deep(p) {
  margin: 0 0 14px 0;
  color: #374151;
  line-height: 1.75;
}

:deep(p:last-child) {
  margin-bottom: 0;
}

:deep(h1),
:deep(h2),
:deep(h3),
:deep(h4),
:deep(h5),
:deep(h6) {
  color: #111827;
  font-weight: 700;
  line-height: 1.35;
}

:deep(h1) {
  font-size: 24px;
  margin: 24px 0 14px 0;
}

:deep(h2) {
  font-size: 20px;
  margin: 22px 0 12px 0;
}

:deep(h3) {
  font-size: 18px;
  margin: 20px 0 10px 0;
}

:deep(h4) {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 16px 0 8px 0;
}

:deep(h5) {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin: 14px 0 6px 0;
}

:deep(h6) {
  font-size: 14px;
  font-weight: 600;
  color: #4b5563;
  margin: 12px 0 6px 0;
}

:deep(strong) {
  font-weight: 600;
  color: #111827;
}

:deep(em) {
  font-style: italic;
}

/* 表格樣式 (完全比照後台/富文本表格) */
:deep(figure.table) {
  margin: 16px 0 20px 0;
  width: 100%;
  overflow-x: auto;
  display: block;
}

:deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0 20px 0;
  font-size: 14px;
  border: 1px solid #d1d5db;
  text-align: left;
}

:deep(th) {
  background-color: #f8fafc;
  color: #111827;
  font-weight: 600;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  text-align: left;
  vertical-align: middle;
}

:deep(td) {
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  color: #374151;
  line-height: 1.6;
  vertical-align: top;
}

:deep(tbody tr:nth-child(even)) {
  background-color: #fcfcfc;
}

/* 引用區塊樣式 (比照後台左側灰色直條) */
:deep(blockquote) {
  border-left: 3px solid #cbd5e1;
  margin: 14px 0 16px 0;
  padding: 4px 0 4px 16px;
  color: #4b5563;
  font-style: normal;
  background-color: transparent;
}

:deep(blockquote p) {
  margin: 0 0 6px 0;
  color: #4b5563;
}

:deep(blockquote p:last-child) {
  margin-bottom: 0;
}

:deep(blockquote ol),
:deep(blockquote ul) {
  margin: 0;
  padding-left: 20px;
}

/* 清單樣式 */
:deep(ol) {
  list-style-type: decimal;
  padding-left: 24px;
  margin: 10px 0 14px 0;
}

:deep(ul) {
  list-style-type: disc;
  padding-left: 24px;
  margin: 10px 0 14px 0;
}

:deep(li) {
  margin-bottom: 6px;
  line-height: 1.7;
  color: #374151;
}

:deep(li > ol),
:deep(li > ul) {
  margin: 4px 0 6px 0;
}

:deep(a) {
  color: var(--color-primary, #022964);
  text-decoration: underline;
}

.modal-footer-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0 4px;
  width: 100%;
}

.checkbox-container {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #1f2937;
}

.button-group {
  display: flex;
  gap: 12px;
}

@media (max-width: 640px) {
  .modal-content {
    padding: 16px 14px;
    max-height: 55vh;
  }

  .modal-footer-container {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
  }

  .button-group {
    justify-content: flex-end;
  }
}
</style>
